import React, { useEffect, useState } from 'react';
import {
  TextField, Select, MenuItem, Button, InputLabel, FormControl,
  SelectChangeEvent, FormHelperText,
} from '@mui/material';
import { Departement, Reglement, Tiers } from '../../models/models';
import { TypeReglement } from '../../constants/enums';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from '../../components/SnackbarProvider';
import { createReglement, setSelectedReglement } from '../../reduxStore/ReglementSlice';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import TiersAutoC from '../AutoComplete/TiersAutoC';
import SelectDepartement from '../Selects/SelectDepartement';

/**
 * Formulaire d'opération diverse (OD) : écriture sur un compte tiers SANS caisse.
 * Stockée comme un `reglement` de type OPERATION_DIVERSE — le backend force
 * caisse_id/mode_paiement_id à NULL. Le `sens` saisi pilote le solde du compte.
 */
interface OperationDiverseFormProps {
  onCancel: () => void;
  /** Pré-remplit le tiers (ex. « Saisir un dû » depuis la page Employés). */
  initialTiers?: Tiers | null;
}

export type SensOption = { value: 'C' | 'D'; label: string };

/**
 * Actions métier adaptées au type de tiers — l'utilisateur choisit une intention
 * claire, le `sens` (C/D) correct est déduit. Aligné sur la convention caisse :
 * encaissement client = C, paiement fournisseur = D.
 */
export const getActionOptions = (tiersType?: string): SensOption[] => {
  switch (tiersType) {
    case 'client':
      return [
        { value: 'C', label: 'Le client a payé / avoir (diminue sa créance)' },
        { value: 'D', label: 'Refacturation / frais (augmente sa créance)' },
      ];
    case 'fournisseur':
      return [
        { value: 'D', label: 'On a payé le fournisseur (diminue notre dette)' },
        { value: 'C', label: 'Facture / charge fournisseur (augmente notre dette)' },
      ];
    case 'employe':
      return [
        { value: 'C', label: "Salaire / prime dû (augmente ce qu'on lui doit)" },
        { value: 'D', label: "Avance / régularisation (diminue ce qu'on lui doit)" },
      ];
    default:
      return [];
  }
};

const OperationDiverseForm: React.FC<OperationDiverseFormProps> = ({ onCancel, initialTiers }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { selectedReglement } = useSelector((state: RootState) => state.ReglementSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);

  const buildEmpty = (): Reglement => ({
    id: -1,
    type_reglement: TypeReglement.OPERATION_DIVERSE,
    date_reglement: new Date().toISOString().split('T')[0],
    numero_reglement: '',
    montant: 0,
    sens: '' as any, // choisi via l'action métier (dépend du type de tiers)
    reference: '',
    mode_paiement_id: null,
    nature_id: null,
    caisse_id: null,
    tiers_id: initialTiers?.id ?? null,
    tiers: initialTiers ?? null,
    departement_id: selectedDepartement?.id ?? null,
    departement: selectedDepartement ?? null,
  });

  const [formData, setFormData] = useState<Reglement>(buildEmpty);

  useEffect(() => {
    if (selectedReglement) {
      setFormData({
        ...selectedReglement,
        date_reglement: new Date(selectedReglement.date_reglement).toISOString().split('T')[0],
      });
    } else {
      setFormData(buildEmpty());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReglement, selectedDepartement, initialTiers]);

  const handleInputChange =
    (field: keyof Reglement) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

  const handleSensChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, sens: event.target.value as 'D' | 'C' }));
  };

  const handleTiersChange = (tiers: Tiers | null) => {
    setFormData(prev => ({
      ...prev,
      tiers_id: tiers ? tiers.id : null,
      tiers: tiers ?? null,
      // L'action (et donc le sens) dépend du type de tiers → on réinitialise
      sens: '' as any,
    }));
  };

  const handleDepartementChange = (dept: Departement | null) => {
    setFormData(prev => ({
      ...prev,
      departement_id: dept ? dept.id : null,
      departement: dept ?? null,
    }));
  };

  const validate = (): string[] => {
    const messages: string[] = [];
    if (!formData.tiers_id) messages.push('Le tiers est obligatoire.');
    if (!formData.departement_id) messages.push('Le département est obligatoire.');
    if (!formData.date_reglement) messages.push('La date est obligatoire.');
    if (!formData.montant || Number(formData.montant) <= 0) messages.push('Le montant doit être strictement positif.');
    if (formData.sens !== 'D' && formData.sens !== 'C') messages.push('Le sens (Débit/Crédit) est obligatoire.');
    return messages;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const messages = validate();
    if (messages.length) {
      messages.forEach(m => showSnackbar(m, 'error'));
      return;
    }
    try {
      // caisse_id/mode_paiement_id forcés à NULL côté service — on les laisse null ici.
      await dispatch(createReglement({ ...formData, caisse_id: null, mode_paiement_id: null })).unwrap();
      showSnackbar('Opération diverse enregistrée avec succès !', 'success');
      dispatch(setSelectedReglement(null));
      onCancel();
    } catch (error) {
      showSnackbar((error as string) || "Erreur lors de l'enregistrement", 'error');
    }
  };

  const isCreating = formData.id === -1;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500, padding: 16 }}>
      <TextField
        label="Date"
        type="date"
        value={formData.date_reglement || ''}
        onChange={handleInputChange('date_reglement')}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required
        size="small"
      />

      {!isCreating && (
        <TextField
          label="Numéro"
          value={formData.numero_reglement || ''}
          InputProps={{ readOnly: true }}
          fullWidth
          size="small"
        />
      )}

      {/* En mode "Tous départements" (aucun département actif), on doit en choisir
          un : reglement.departement_id est obligatoire. */}
      {!selectedDepartement && (
        <SelectDepartement
          onDepartementChange={handleDepartementChange}
          selectedDepartement={formData.departement}
        />
      )}

      <TiersAutoC
        onTiersChange={handleTiersChange}
        value={formData.tiers}
        required
      />

      <TextField
        label="Montant"
        type="number"
        value={formData.montant || ''}
        onChange={handleInputChange('montant')}
        fullWidth
        required
        size="small"
      />

      <FormControl fullWidth size="small" required disabled={!formData.tiers_id}>
        <InputLabel id="od_action_label">Opération</InputLabel>
        <Select
          labelId="od_action_label"
          value={formData.sens || ''}
          onChange={handleSensChange}
          label="Opération"
          size="small"
        >
          {getActionOptions(formData.tiers?.type).map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {formData.tiers_id
            ? "Choisissez la nature de l'opération."
            : "Sélectionnez d'abord un tiers."}
        </FormHelperText>
      </FormControl>

      <TextField
        label="Libellé / Référence"
        value={formData.reference || ''}
        onChange={handleInputChange('reference')}
        fullWidth
        size="small"
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="contained" color="primary" type="submit">
          {isCreating ? 'Créer' : 'Modifier'}
        </Button>
        <Button variant="outlined" color="inherit" type="button" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
};

export default OperationDiverseForm;
